import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  FiArrowRight,
  FiCheck,
  FiCode,
  FiDatabase,
  FiLayers,
  FiLock,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiShield,
  FiCpu,
  FiUsers,
} from "react-icons/fi";
import SEO from "../../components/shared/seo";
import { LayoutLP } from "../../components/layouts/layout-lp";
import { LogoText } from "../../components/shared/logo";
import heroImage from "../../images/DIVIV-35.jpg";
import processImage from "../../images/contactus-img.jpg";

const buildCards = [
  {
    icon: FiDatabase,
    title: "Modernise legacy databases",
    description:
      "Rebuild aging systems into stable, high-performance database platforms designed around your current business workflows.",
    points: [
      "SQL refactoring and schema redesign",
      "Migration planning with low operational risk",
      "Performance, resilience, and maintainability first",
    ],
  },
  {
    icon: FiRefreshCw,
    title: "Reliable backend solutions",
    description:
      "Backend services, APIs, and data flows that connect business systems cleanly without brittle manual workarounds.",
    points: [
      "ERP, CRM, CMS, and portal integrations",
      "Automation for repetitive data handling",
      "Secure backend logic for business-critical workflows",
    ],
  },
  {
    icon: FiCode,
    title: "Cloud-native database systems",
    description:
      "Purpose-built database and backend architecture for new platforms that need to scale, integrate, and stay secure.",
    points: [
      "SQL, PostgreSQL, and NoSQL expertise",
      "Secure enterprise data design",
      "Built to evolve with your team and stack",
    ],
  },
];

const valueCards = [
  {
    icon: FiCpu,
    title: "Expert database architects",
    description:
      "We design data models, service boundaries, and failure handling before code is rushed into production.",
  },
  {
    icon: FiLock,
    title: "Secure enterprise data design",
    description:
      "Access control, auditability, and operational safety are built into the platform design, not bolted on later.",
  },
  {
    icon: FiShield,
    title: "High-performance backend delivery",
    description:
      "We build reliable backend systems that support real business loads, legacy constraints, and future growth.",
  },
];

const processCards = [
  {
    icon: FiSearch,
    title: "Discovery & audit",
    description:
      "We review your current database, backend pain points, performance issues, and business workflow requirements.",
  },
  {
    icon: FiLayers,
    title: "Architecture & database design",
    description:
      "We shape the target architecture, data model, integration approach, and security boundaries.",
  },
  {
    icon: FiSettings,
    title: "Build, migrate & integrate",
    description:
      "We implement the backend, migrate data where needed, and connect the platform into your wider stack.",
    image: processImage,
  },
  {
    icon: FiCheck,
    title: "Stabilise & improve",
    description:
      "We monitor, optimise, and iterate once the solution is live so the system keeps delivering reliably.",
  },
];

const audienceCards = [
  {
    icon: FiDatabase,
    title: "Legacy platform owners",
    description:
      "Businesses that need to modernise older databases or backend systems without interrupting critical operations.",
  },
  {
    icon: FiUsers,
    title: "Operations-heavy teams",
    description:
      "Teams that depend on dependable data flows across operations, reporting, finance, logistics, or service delivery.",
  },
  {
    icon: FiShield,
    title: "Security-conscious enterprises",
    description:
      "Organisations that need backend systems with stronger governance, permissions, traceability, and resilience.",
  },
  {
    icon: FiRefreshCw,
    title: "Businesses needing a database consultant",
    description:
      "Companies looking for senior guidance on database architecture, backend strategy, migration, and implementation.",
  },
];

const testimonials = [
  {
    quote:
      "Our aging Access database was sitting on outdated hardware but was pivotal to our organisation. Diviv helped us migrate our research data onto a more supportable and modern MySQL system.",
    name: "Clint Dieger",
    role: "Operations Manager",
  },
];

function CustomDatabaseBackendDevelopment() {
  return (
    <>
      <SEO
        title="High-Performance Backend Developers and Database Architects"
        description="Custom high-performance databases tailored to your unique business workflows. Modernise legacy systems with SQL, PostgreSQL, and NoSQL experts."
        keywords="database consultant, backend developer, custom database developer, database development services, modernise legacy databases"
      />
      <LayoutLP hideFooterBanner>
        <div
          id="custom-database-backend-content"
          className="page-layout page-layout--custom-database-lp"
          data-menu-class="inverted"
        >
          <section
            className="custom-db-lp__hero"
            style={{
              backgroundImage: `linear-gradient(90deg, rgba(20, 32, 27, 0.74) 0%, rgba(20, 32, 27, 0.4) 42%, rgba(20, 32, 27, 0.12) 100%), url(${heroImage})`,
            }}
          >
            <Container>
              {/* <div className="custom-db-lp__brand-pill">
                <LogoText fill="#18362d" />
              </div> */}
              <div className="custom-db-lp__hero-copy">
                <span className="custom-db-lp__eyebrow">
                  Database & Backend Authority
                </span>
                <h1>
                  High-performance backend developers and expert database
                  architects
                </h1>
                <p>
                  Custom high-performance databases tailored to your unique
                  business workflows. Modernise legacy systems with secure,
                  reliable backend solutions.
                </p>
                <div className="custom-db-lp__hero-actions" id="contact-cta">
                  <a
                    href="/contactus"
                    className="custom-db-lp__button custom-db-lp__button--primary"
                  >
                    Discuss your project <FiArrowRight />
                  </a>
                  <a
                    href="mailto:hello@diviv.com.au"
                    className="custom-db-lp__button custom-db-lp__button--secondary"
                  >
                    hello@diviv.com.au
                  </a>
                </div>
              </div>
            </Container>
          </section>

          <div className="custom-db-lp__divider" />

          <section className="custom-db-lp__section custom-db-lp__section--light">
            <Container>
              <div className="custom-db-lp__section-heading text-center">
                <h2>What We Build</h2>
                <p>
                  Database and backend systems built for performance, security,
                  and long-term operational reliability.
                </p>
              </div>
              <Row>
                {buildCards.map(card => {
                  const Icon = card.icon;
                  return (
                    <Col
                      lg={4}
                      md={6}
                      sm={12}
                      key={card.title}
                      className="mb-4"
                    >
                      <article className="custom-db-lp__build-card">
                        <Icon className="custom-db-lp__build-card-icon" />
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                        <ul>
                          {card.points.map(point => (
                            <li key={point}>
                              <FiCheck />
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </article>
                    </Col>
                  );
                })}
              </Row>
            </Container>
          </section>

          <div className="custom-db-lp__divider custom-db-lp__divider--dark" />

          <section className="custom-db-lp__section custom-db-lp__section--dark custom-db-lp__section--value">
            <Container>
              <Row className="align-items-start">
                <Col lg={5} sm={12} className="mb-5 mb-lg-0">
                  <div className="custom-db-lp__intro-block">
                    <h2>Why Diviv?</h2>
                    <p>
                      We help businesses that need more than a generic
                      developer. If you are looking for a database consultant,
                      senior backend engineers, or a partner to modernise legacy
                      systems, we build around business outcomes and operational
                      reality.
                    </p>
                  </div>
                </Col>
                <Col lg={7} sm={12}>
                  {valueCards.map(card => {
                    const Icon = card.icon;
                    return (
                      <article
                        className="custom-db-lp__value-card"
                        key={card.title}
                      >
                        <div className="custom-db-lp__value-card-icon-wrap">
                          <Icon className="custom-db-lp__value-card-icon" />
                        </div>
                        <div>
                          <h3>{card.title}</h3>
                          <p>{card.description}</p>
                        </div>
                      </article>
                    );
                  })}
                </Col>
              </Row>
            </Container>
          </section>

          <section className="custom-db-lp__section custom-db-lp__section--light custom-db-lp__section--process">
            <Container>
              <div className="custom-db-lp__section-heading text-center">
                <h2>How We Work</h2>
              </div>
              <Row>
                {processCards.map(card => {
                  const Icon = card.icon;
                  const hasImage = Boolean(card.image);
                  return (
                    <Col
                      lg={3}
                      md={6}
                      sm={12}
                      key={card.title}
                      className="mb-4"
                    >
                      <article
                        className={
                          "custom-db-lp__process-card" +
                          (hasImage ? " custom-db-lp__process-card--image" : "")
                        }
                        style={
                          hasImage
                            ? {
                              backgroundImage: `linear-gradient(180deg, rgba(12, 23, 19, 0.16) 0%, rgba(12, 23, 19, 0.56) 100%), url(${card.image})`,
                            }
                            : undefined
                        }
                      >
                        <Icon className="custom-db-lp__process-card-icon" />
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                        {/* <span className="custom-db-lp__process-card-arrow">
                          <FiArrowRight />
                        </span> */}
                      </article>
                    </Col>
                  );
                })}
              </Row>
            </Container>
          </section>

          <section className="custom-db-lp__section custom-db-lp__section--dark custom-db-lp__section--audience">
            <Container>
              <div className="custom-db-lp__section-heading text-center custom-db-lp__section-heading--inverse">
                <h2>Who It&apos;s For</h2>
              </div>
              <div className="custom-db-lp__audience-grid">
                <aside className="custom-db-lp__audience-quote">
                  <span className="custom-db-lp__quote-mark">"</span>
                  <p>
                    If your backend needs to be fast, secure, and built around
                    real workflows, this is where we fit.
                  </p>
                </aside>
                <div className="custom-db-lp__audience-cards">
                  {audienceCards.map(card => {
                    const Icon = card.icon;
                    return (
                      <article
                        className="custom-db-lp__audience-card"
                        key={card.title}
                      >
                        <Icon className="custom-db-lp__audience-card-icon" />
                        <h3>{card.title}</h3>
                        <p>{card.description}</p>
                      </article>
                    );
                  })}
                </div>
              </div>
            </Container>
          </section>

          <section className="custom-db-lp__section custom-db-lp__section--light custom-db-lp__section--testimonials">
            <Container>
              <div className="custom-db-lp__section-heading text-center">
                <h2>What Clients Say</h2>
              </div>
              <Row>
                {testimonials.slice(0, 3).map(testimonial => (
                  <Col
                    lg={testimonials.length === 1 ? 12 : 4}
                    md={testimonials.length === 1 ? 12 : 6}
                    sm={12}
                    key={testimonial.quote}
                    className="mb-4"
                  >
                    <article className="custom-db-lp__testimonial-card">
                      <span className="custom-db-lp__testimonial-mark">"</span>
                      <p className="custom-db-lp__testimonial-quote">
                        {testimonial.quote}
                      </p>
                      <div className="custom-db-lp__testimonial-meta">
                        <strong>{testimonial.name}</strong>
                        <span>{testimonial.role}</span>
                      </div>
                    </article>
                  </Col>
                ))}
              </Row>
            </Container>
          </section>

          <section className="custom-db-lp__closing">
            <Container>
              <div className="custom-db-lp__closing-inner">
                <div className="custom-db-lp__closing-copy">
                  <p>
                    Talk to senior backend developers and database architects
                    about your current systems, migration plans, or next
                    platform build.
                  </p>
                  <div className="custom-db-lp__hero-actions">
                    <a
                      href="/contactus"
                      className="custom-db-lp__button custom-db-lp__button--primary"
                    >
                      Discuss your project <FiArrowRight />
                    </a>
                    <a
                      href="mailto:hello@diviv.com.au"
                      className="custom-db-lp__button custom-db-lp__button--secondary"
                    >
                      hello@diviv.com.au
                    </a>
                  </div>
                </div>
                <div className="custom-db-lp__closing-logo">
                  <LogoText fill="#10271f" />
                </div>
              </div>
            </Container>
          </section>
        </div>
      </LayoutLP>
    </>
  );
}

export default CustomDatabaseBackendDevelopment;
